import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Globe, PieChart, Coins } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { useLanguage } from '../../context/LanguageContext';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const WhyGoldSection = () => {
  const { t } = useLanguage();

  const reasons = [
    {
      icon: TrendingUp,
      titleKey: 'inflationHedge',
      descKey: 'inflationHedgeDesc',
    },
    {
      icon: Globe,
      titleKey: 'globallyRecognized',
      descKey: 'globallyRecognizedDesc',
    },
    {
      icon: PieChart,
      titleKey: 'portfolioDiversification',
      descKey: 'portfolioDiversificationDesc',
    },
    {
      icon: Coins,
      titleKey: 'highlyLiquid',
      descKey: 'highlyLiquidDesc',
    },
  ];

  return (
    <section id="why-gold" className="py-16 md:py-24 bg-[#0a0a0f]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-10 md:mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('whyGoldTitle')}
          </h2>
          <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto px-4">
            {t('whyGoldSubtitle')}
          </p>
        </motion.div>

        {/* Cards Grid */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <Card
                  className="group h-full bg-[#12121a] border border-[#1e1e2e] hover:border-[#D4AF37]/30 rounded-2xl overflow-hidden transition-all duration-300"
                >
                  <CardContent className="p-5 md:p-6 relative h-full">
                    {/* Glow effect on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/0 group-hover:from-[#D4AF37]/5 group-hover:to-transparent transition-all duration-500" />
                    
                    <div className="relative">
                      <motion.div 
                        className="w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 md:mb-5 group-hover:bg-[#D4AF37]/20 transition-all duration-300"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <Icon className="w-6 h-6 md:w-7 md:h-7 text-[#D4AF37]" />
                      </motion.div>
                      <h3 className="text-base md:text-lg font-semibold text-white mb-2 md:mb-3 group-hover:text-[#D4AF37] transition-colors">
                        {t(reason.titleKey)}
                      </h3>
                      <p className="text-sm text-gray-400 leading-relaxed">
                        {t(reason.descKey)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom Note */}
        <motion.p 
          className="text-center text-xs md:text-sm text-gray-500 mt-8 md:mt-12 px-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          {t('basedOnHistorical')}
        </motion.p>
      </div>
    </section>
  );
};

export default WhyGoldSection;
